/**
 * Shared CLI helpers for superadmin management commands (knobs, feature-flags).
 */

import fs from 'node:fs/promises';
import path from 'node:path';
import process from 'node:process';
import { getCanaryTmpDir } from '@chatsdet/tmp';
import { VALID_MACRO_NAMES } from '@chatsdet/types';
import { getArgValue } from './auth.js';
import { apiFetch, exitOnFailure, isTerminalFailure } from './api-fetch.js';

export type LifecycleStage = 'active' | 'baking' | 'internal' | 'deprecated' | 'ready_for_cleanup';

/** Stages knobs may use — `baking` (the oven) and `internal` are flag-only. */
export const KNOB_LIFECYCLE_STAGES: LifecycleStage[] = [
  'active',
  'deprecated',
  'ready_for_cleanup',
];

export function toLifecycleLabel(stage: LifecycleStage): string {
  switch (stage) {
    case 'baking':
      return 'baking';
    case 'internal':
      return 'internal';
    case 'deprecated':
      return 'deprecated';
    case 'ready_for_cleanup':
      return 'ready_for_cleanup';
    default:
      return 'active';
  }
}

export function parseLifecycleStage(
  argv: string[],
  allowedStages: LifecycleStage[] = [
    'active',
    'baking',
    'internal',
    'deprecated',
    'ready_for_cleanup',
  ]
): LifecycleStage {
  const stage = getArgValue(argv, '--stage');
  if (!stage || !allowedStages.includes(stage as LifecycleStage)) {
    console.error(`Error: --stage is required and must be one of: ${allowedStages.join(', ')}`);
    process.exit(1);
  }
  return stage as LifecycleStage;
}

/**
 * Target-org id for superadmin cross-org commands (feature-flag, product,
 * account, task). `--org` is now the global identity selector — which of *your*
 * logged-in orgs to act as — so cross-org targeting uses `--target-org`.
 *
 * The two flags are mutually exclusive by design: a bare `--org` naming an org
 * you aren't logged into is already rejected by identity resolution (with a hint
 * to use `--target-org`); if it names one you ARE logged into, we stop here so a
 * superadmin action never silently runs under the wrong identity. Either way the
 * fix is the same — use `--target-org`.
 */
export function getTargetOrg(argv: string[]): string | undefined {
  const target = getArgValue(argv, '--target-org');
  if (target) return target;
  if (getArgValue(argv, '--org')) {
    console.error(
      'Error: --org selects which of your logged-in orgs to act as. To target a ' +
        'different org for this command, use --target-org.'
    );
    process.exit(1);
  }
  return undefined;
}

export async function apiRequest<T extends { ok: boolean; error?: string }>(
  apiUrl: string,
  token: string,
  method: string,
  path: string,
  body?: Record<string, unknown>,
  options?: { timeoutMs?: number }
): Promise<T> {
  const result = await apiFetch(`${apiUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'User-Agent': 'Cofactor-CLI',
    },
    token,
    ...(options?.timeoutMs !== undefined ? { timeoutMs: options.timeoutMs } : {}),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!result.ok) {
    const { failure } = result;
    // Terminal kinds (auth/proxy/TLS) surface the full diagnostic and exit;
    // http-error falls through so the caller can pretty-print structured
    // user errors (unresolved vars, invalid macros, etc.).
    if (isTerminalFailure(failure)) exitOnFailure(failure);
    const text = failure.body ?? '';
    let errorMessage = failure.message;
    let parsed: Record<string, unknown> | null = null;
    try {
      const data = JSON.parse(text);
      if (data && typeof data === 'object') {
        parsed = data as Record<string, unknown>;
        if (typeof parsed.error === 'string') errorMessage = parsed.error;
      }
    } catch {
      /* keep failure.message */
    }
    return { ...parsed, ok: false, error: errorMessage } as T;
  }

  try {
    return (await result.response.json()) as T;
  } catch {
    return { ok: false, error: `Invalid JSON response (${result.response.status})` } as T;
  }
}

/** One environment option carried by the workflow-detail payload. */
export type WorkflowEnvOption = {
  id: string;
  name: string;
  type: string;
  isDefault: boolean;
  environmentKindId?: string | null;
  environmentKindName?: string | null;
};

/**
 * Fetch the environments available on a workflow's property (from the
 * workflow-detail payload). Shared by `workflow run --environment` and
 * `workflow patch --default-environment` so both resolve names identically.
 */
export async function fetchWorkflowEnvironments(
  apiUrl: string,
  token: string,
  workflowId: string
): Promise<WorkflowEnvOption[]> {
  const res = await apiRequest<{
    ok: boolean;
    error?: string;
    workflow?: { environments?: WorkflowEnvOption[] };
  }>(apiUrl, token, 'GET', `/workflows/${workflowId}`);
  return res.workflow?.environments ?? [];
}

/**
 * Resolve a `--environment` argument (UUID or case-insensitive name) to an
 * environment id against the flow's property. Exits with an actionable error
 * listing the known environments when no match is found.
 */
export function resolveEnvironmentByNameOrId(envs: WorkflowEnvOption[], nameOrId: string): string {
  const match =
    envs.find((e) => e.id === nameOrId) ??
    envs.find((e) => e.name.toLowerCase() === nameOrId.toLowerCase()) ??
    envs.find((e) => e.environmentKindName?.toLowerCase() === nameOrId.toLowerCase());
  if (match) return match.id;
  // A bare UUID that isn't in the (possibly empty) known list may still be a
  // valid env the server will accept — e.g. a login-less flow with no resolved
  // property scope being pointed at a sandbox env. Pass it through and let the
  // server be the authority; only hard-fail on an unresolvable *name*.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(nameOrId)) {
    return nameOrId;
  }
  console.error(
    `Error: environment "${nameOrId}" not found on this flow's property. Known: ${
      envs.map((e) => `${e.name} (${e.type})`).join(', ') || '(none)'
    }`
  );
  process.exit(1);
}

type UnresolvedRef = {
  nodeId?: string;
  nodeType?: string;
  field?: string;
  variable: string;
};

/**
 * Render a structured `userError` response from the workflow API into a
 * multi-line, actionable message. Recognizes `WORKFLOW_UNRESOLVED_VARIABLES`
 * and falls back to `✖ {code}: {message}` for other codes. Returns `null` if
 * the response body isn't a recognized structured error.
 */
export function renderWorkflowUserError(result: Record<string, unknown>): string | null {
  const code = typeof result.code === 'string' ? result.code : null;
  const message = typeof result.error === 'string' ? result.error : null;
  if (!code) return null;

  if (code === 'WORKFLOW_VALIDATION_FAILED') {
    type ValidationIssue = {
      nodeId?: string | null;
      nodeType?: string | null;
      field?: string;
      message?: string;
      code?: string;
      severity?: 'error' | 'warning';
    };
    const issues = Array.isArray(result.issues) ? (result.issues as ValidationIssue[]) : [];
    const errors = issues.filter((i) => (i.severity ?? 'error') === 'error');
    const warnings = issues.filter((i) => i.severity === 'warning');

    const lines: string[] = [
      `✖ Validation failed (${errors.length} error${errors.length === 1 ? '' : 's'}` +
        (warnings.length > 0
          ? `, ${warnings.length} warning${warnings.length === 1 ? '' : 's'}`
          : '') +
        ')',
    ];
    if (message) lines.push(`  ${message}`);

    // Group by nodeId (null = flow-level).
    const byNode = new Map<string, ValidationIssue[]>();
    for (const issue of issues) {
      const key = issue.nodeId ?? '__flow__';
      const arr = byNode.get(key) ?? [];
      arr.push(issue);
      byNode.set(key, arr);
    }
    for (const [nodeKey, nodeIssues] of byNode) {
      const heading =
        nodeKey === '__flow__'
          ? 'Flow'
          : `Node ${nodeKey}${nodeIssues[0]?.nodeType ? ` (${nodeIssues[0].nodeType})` : ''}`;
      lines.push(`  ${heading}:`);
      for (const issue of nodeIssues) {
        const sev = issue.severity === 'warning' ? '⚠' : '•';
        const field = issue.field ? `${issue.field} — ` : '';
        lines.push(`    ${sev} ${field}${issue.message ?? ''}`);
      }
    }
    return lines.join('\n');
  }

  if (code === 'WORKFLOW_UNRESOLVED_VARIABLES') {
    const lines: string[] = [`✖ ${code}`];
    if (message) lines.push(`  ${message}`);
    const refs = Array.isArray(result.refs) ? (result.refs as UnresolvedRef[]) : [];
    const variables = Array.isArray(result.variables)
      ? (result.variables as string[])
      : refs.map((r) => r.variable);

    if (refs.length > 0) {
      lines.push('  Unresolved references:');
      for (const ref of refs) {
        const loc = ref.field && ref.nodeId ? ` (node ${ref.nodeId}, ${ref.field})` : '';
        lines.push(`    • $var.${ref.variable}${loc}`);
      }
    } else if (variables.length > 0) {
      lines.push('  Unresolved:');
      for (const v of variables) lines.push(`    • $var.${v}`);
    }

    const macroHints = variables.filter((v) => VALID_MACRO_NAMES.has(v.toUpperCase()));
    if (macroHints.length > 0) {
      lines.push('  Hint: these look like macro names, not variables:');
      for (const v of macroHints) {
        lines.push(`    $var.${v}  →  $${v.toUpperCase()}()`);
      }
    }
    return lines.join('\n');
  }

  if (code === 'WORKFLOW_INVALID_INPUT_VALUES') {
    const lines: string[] = [`✖ ${code}`];
    if (message) lines.push(`  ${message}`);
    const issues = Array.isArray(result.issues)
      ? (result.issues as Array<{
          key?: string;
          value?: string;
          reason?: string;
          variables?: string[];
        }>)
      : [];
    for (const issue of issues) {
      const key = issue.key ?? '?';
      const value = issue.value ?? '';
      if (issue.reason === 'self_referential') {
        lines.push(`  • ${key}: self-referential  →  ${value}`);
        lines.push(`    Use a literal or point at a different upstream variable.`);
      } else if (issue.reason === 'macro_name_used_as_var') {
        lines.push(`  • ${key}: macro name used as $var  →  ${value}`);
        for (const v of issue.variables ?? []) {
          lines.push(`    $var.${v}  →  $${v.toUpperCase()}()`);
        }
      } else {
        lines.push(`  • ${key}: ${issue.reason ?? 'invalid'}  →  ${value}`);
      }
    }
    return lines.join('\n');
  }

  if (code === 'WORKFLOW_INVALID_MACRO') {
    const lines: string[] = [`✖ ${code}`];
    if (message) lines.push(`  ${message}`);
    const invalid = Array.isArray(result.invalid) ? (result.invalid as string[]) : [];
    const valid = Array.isArray(result.valid) ? (result.valid as string[]) : [];
    if (invalid.length > 0) {
      lines.push(`  Invalid macro(s): ${invalid.map((n) => `$${n}()`).join(', ')}`);
    }
    if (valid.length > 0) {
      lines.push(`  Valid macros: ${valid.map((n) => `$${n}()`).join(', ')}`);
    }
    return lines.join('\n');
  }

  return `✖ ${code}${message ? `: ${message}` : ''}`;
}

export async function downloadStorageState(opts: {
  apiUrl: string;
  token: string;
  propertyId: string;
  credentialId: string;
  prefix?: string;
}): Promise<string | undefined> {
  const tmpFile = path.join(getCanaryTmpDir(), `${opts.prefix ?? 'canary-ss'}-${Date.now()}.json`);
  try {
    const res = await fetch(
      `${opts.apiUrl}/org/properties/${opts.propertyId}/credentials/${opts.credentialId}/storage-state/download`,
      {
        headers: { Authorization: `Bearer ${opts.token}` },
        redirect: 'follow',
      }
    );
    if (res.ok) {
      const body = await res.text();
      await fs.writeFile(tmpFile, body, 'utf-8');
      return tmpFile;
    }
  } catch {
    // Caller handles missing storage state
  }
  return undefined;
}

/* ── Credential selection ─────────────────────────────────────────────── */

export interface CredentialListItem {
  id: string;
  name: string;
  propertyId: string;
  propertyName?: string;
  propertyBaseUrl?: string;
  environmentId?: string | null;
  environmentName?: string | null;
  storageStateS3Key?: string | null;
  isDefault?: boolean;
  isEphemeral?: boolean;
}

/**
 * Fetch org credentials and let the user pick one interactively.
 *
 * @param credentialArg - Credential name or ID to auto-select, or undefined for interactive
 * @returns The selected credential, or null if none found / invalid selection
 */
export async function selectCredential(
  apiUrl: string,
  token: string,
  credentialArg?: string
): Promise<CredentialListItem | null> {
  const credentials = await fetchList<CredentialListItem>(
    apiUrl,
    token,
    '/org/credentials',
    'items'
  );

  if (credentials.length === 0) {
    console.error('No credentials found. Create one first in the web app.');
    return null;
  }

  if (credentialArg) {
    const match = credentials.find(
      (c) => c.id === credentialArg || c.name.toLowerCase() === credentialArg.toLowerCase()
    );
    if (!match) {
      console.error(`Credential "${credentialArg}" not found.`);
      console.error('Available credentials:');
      for (const c of credentials) {
        const env = c.environmentName ? ` — ${c.environmentName}` : '';
        const baseUrl = c.propertyBaseUrl ? ` — ${c.propertyBaseUrl}` : '';
        console.error(`  - ${c.name} [${c.propertyName ?? c.propertyId}${env}${baseUrl}]`);
      }
      return null;
    }
    return match;
  }

  // Interactive selection
  console.log('\nSelect a credential:\n');
  const labels = credentials.map((c) => {
    const property = c.propertyName ?? c.propertyId;
    const env = c.environmentName ? ` — ${c.environmentName}` : '';
    const baseUrl = c.propertyBaseUrl ? ` — ${c.propertyBaseUrl}` : '';
    return `${c.name} [${property}${env}${baseUrl}]`;
  });

  const readline = await import('node:readline');
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  const answer = await new Promise<string>((resolve) => {
    for (let i = 0; i < labels.length; i++) {
      console.log(`  ${i + 1}. ${labels[i]}`);
    }
    rl.question('\nEnter number: ', (ans) => {
      rl.close();
      resolve(ans.trim());
    });
  });

  const idx = parseInt(answer, 10) - 1;
  if (isNaN(idx) || idx < 0 || idx >= credentials.length) {
    console.error('Invalid selection.');
    return null;
  }

  return credentials[idx];
}

/* ── Property selection ──────────────────────────────────────────────── */

interface PropertyListItem {
  id: string;
  name: string;
  baseUrl: string;
}

export async function fetchProperties(apiUrl: string, token: string): Promise<PropertyListItem[]> {
  return fetchList<PropertyListItem>(apiUrl, token, '/org/properties', 'items');
}

/**
 * Read the application/property selector flag. `--application` is the user-facing
 * name; `--property` is kept as a backwards-compat alias (read second). Internally
 * this is still a "property" — the rename is cosmetic.
 */
export function getApplicationArg(argv: string[]): string | undefined {
  return getArgValue(argv, '--application') ?? getArgValue(argv, '--property');
}

/**
 * Read the application/property UUID flag. `--application-id` is the user-facing
 * name; `--property-id` is kept as a backwards-compat alias.
 */
export function getApplicationIdArg(argv: string[]): string | undefined {
  return getArgValue(argv, '--application-id') ?? getArgValue(argv, '--property-id');
}

export async function selectProperty(
  apiUrl: string,
  token: string,
  hint?: string
): Promise<PropertyListItem | null> {
  const properties = await fetchProperties(apiUrl, token);

  if (properties.length === 0) {
    console.error('No applications found. Create one first in the web app.');
    return null;
  }

  if (hint) {
    const match = properties.find(
      (p) => p.id === hint || p.name.toLowerCase() === hint.toLowerCase()
    );
    if (!match) {
      console.error(`Application "${hint}" not found.`);
      console.error('Available applications:');
      for (const p of properties) {
        console.error(`  - ${p.name} (${p.baseUrl})`);
      }
      return null;
    }
    return match;
  }

  if (properties.length === 1) {
    console.log(`Auto-selected application: ${properties[0].name}`);
    return properties[0];
  }

  return promptChoice(
    'Select an application:',
    properties.map((p) => ({ label: `${p.name} — ${p.baseUrl}`, value: p }))
  );
}

/* ── Interactive prompt helpers ──────────────────────────────────────── */

export async function promptInput(question: string, defaultValue?: string): Promise<string> {
  const readline = await import('node:readline');
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  const prompt = defaultValue ? `${question} [${defaultValue}]: ` : `${question}: `;
  const answer = await new Promise<string>((resolve) => {
    rl.question(prompt, (ans) => {
      rl.close();
      resolve(ans.trim());
    });
  });
  return answer || defaultValue || '';
}

export async function promptChoice<T>(
  question: string,
  options: Array<{ label: string; value: T }>
): Promise<T | null> {
  console.log(`\n${question}\n`);
  for (let i = 0; i < options.length; i++) {
    console.log(`  ${i + 1}. ${options[i].label}`);
  }
  const readline = await import('node:readline');
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  const answer = await new Promise<string>((resolve) => {
    rl.question('\nEnter number: ', (ans) => {
      rl.close();
      resolve(ans.trim());
    });
  });

  const idx = parseInt(answer, 10) - 1;
  if (isNaN(idx) || idx < 0 || idx >= options.length) {
    console.error('Invalid selection.');
    return null;
  }
  return options[idx].value;
}

export async function uploadStorageState(opts: {
  apiUrl: string;
  token: string;
  propertyId: string;
  credentialId: string;
  storageState: unknown;
}): Promise<{ ok: boolean; s3Key?: string; error?: string }> {
  const result = await apiFetch(
    `${opts.apiUrl}/org/properties/${opts.propertyId}/credentials/${opts.credentialId}/storage-state/upload`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${opts.token}`,
        'Content-Type': 'application/json',
      },
      token: opts.token,
      body: JSON.stringify({ storageState: opts.storageState }),
    }
  );

  if (!result.ok) exitOnFailure(result.failure);

  return (await result.response.json()) as { ok: boolean; s3Key?: string; error?: string };
}

export async function fetchList<T>(
  apiUrl: string,
  token: string,
  path: string,
  listKey: string
): Promise<T[]> {
  const result = await apiFetch(`${apiUrl}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
    token,
  });

  if (!result.ok) exitOnFailure(result.failure);

  const json = (await result.response.json()) as Record<string, unknown>;

  if (!json.ok) {
    console.error(`Error: ${json.error}`);
    process.exit(1);
  }

  return (json[listKey] as T[]) ?? [];
}
